import React from "react";
import { motion } from "framer-motion";
import { splitMetrics } from "../lib/metrics";
import { cn } from "../lib/utils";

export const EASE_OUT_EXPO: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const container = "mx-auto w-full max-w-7xl px-5 sm:px-8";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "li";
}

// Fades content up once as it scrolls into view. MotionConfig in App.tsx
// drops the movement for visitors who prefer reduced motion.
export function Reveal({ children, className, delay = 0, as = "div" }: RevealProps) {
  const motionProps = {
    className,
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-64px" },
    transition: { duration: 0.8, delay, ease: EASE_OUT_EXPO },
  };

  return as === "li" ? (
    <motion.li {...motionProps}>{children}</motion.li>
  ) : (
    <motion.div {...motionProps}>{children}</motion.div>
  );
}

export function Eyebrow({ index, children, className }: { index?: string; children: React.ReactNode; className?: string }) {
  return (
    <p className={cn("flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] text-fg-subtle", className)}>
      {index && <span className="text-azure-text">{index}</span>}
      <span aria-hidden="true" className="h-px w-8 bg-edge-strong" />
      {children}
    </p>
  );
}

// The warm half of the type system: serif italic for one word per heading.
export function Accent({ children }: { children: React.ReactNode }) {
  return <em className="font-serif text-[1.08em] font-normal italic tracking-[-0.01em] text-azure-text">{children}</em>;
}

interface SectionHeaderProps {
  index: string;
  eyebrow: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
}

export function SectionHeader({ index, eyebrow, title, lede }: SectionHeaderProps) {
  return (
    <Reveal className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10">
      <div className="lg:col-span-7">
        <Eyebrow index={index}>{eyebrow}</Eyebrow>
        <h2 className="mt-6 text-balance text-[2.6rem] font-semibold leading-[1] tracking-[-0.045em] text-fg sm:text-6xl">
          {title}
        </h2>
      </div>
      {lede && <p className="text-pretty text-lg leading-relaxed text-fg-muted lg:col-span-5 lg:pb-1">{lede}</p>}
    </Reveal>
  );
}

interface MetricTextProps {
  text: string;
  metricClassName?: string;
}

// Renders text with every figure (80%, 4.2, 70K, 0→1) picked out.
export function MetricText({ text, metricClassName = "font-medium text-mint" }: MetricTextProps) {
  return (
    <>
      {splitMetrics(text).map((segment, i) =>
        segment.metric ? (
          <span key={i} className={cn("whitespace-nowrap tabular-nums", metricClassName)}>
            {segment.text}
          </span>
        ) : (
          <React.Fragment key={i}>{segment.text}</React.Fragment>
        )
      )}
    </>
  );
}
